import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { ChevronDown, Calendar, Copy, Trash2 } from 'lucide-react'
import { useMemo, useState, type ReactNode } from 'react'

import type { MarketingContent } from '@/api/admin-marketing-content-types'
import { useCreateMarketingContent, useDeleteMarketingContent, useDuplicateMarketingContent } from '@/api/api-mutations'
import { useListMarketingContent } from '@/api/react-query'
import { Button } from '@/components/base/button'
import { Container, ContainerHeader } from '@/components/base/container'
import { Heading } from '@/components/base/heading'
import { IconButton } from '@/components/base/icon-button'
import { StatusCell } from '@/components/base/status-cell'
import { Text } from '@/components/base/text'
import { toast } from '@/components/base/toast'
import { SingleColumnPage } from '@/components/page-layout'
import { useI18n } from '@/i18n/i18n-react'
import { Plus } from '@/icons/plus'
import { clx } from '@/utils/clx'

export const Route = createFileRoute('/_authenticated/_general/marketing/content/')({
	component: MarketingContentListPage,
	staticData: {
		crumb: ({ context: { L } }) => L.Marketing.Content.Title()
	}
})

type WindowStatus = 'ACTIVE' | 'SCHEDULED' | 'DRAFT' | 'ENDED'

const deriveWindowStatus = (content: MarketingContent, now: number): WindowStatus => {
	if (content.status === 'DRAFT') return 'DRAFT'
	if (content.endsAt && content.endsAt <= now) return 'ENDED'
	if (content.startsAt && content.startsAt > now) return 'SCHEDULED'
	return 'ACTIVE'
}

function MarketingContentListPage() {
	const { L } = useI18n()
	const navigate = useNavigate()

	// Volume is small (a tenant has at most a few dozen content items); render the full set
	// client-side so we can group by derived window status without paginating.
	const contentQuery = useListMarketingContent({ limit: 100, offset: 0 })

	const createContent = useCreateMarketingContent()
	const deleteContent = useDeleteMarketingContent()
	const duplicateContent = useDuplicateMarketingContent()

	const now = Date.now()
	const allRows = contentQuery.data?.content ?? []

	const grouped = useMemo(() => {
		const groups: Record<WindowStatus, MarketingContent[]> = { ACTIVE: [], SCHEDULED: [], DRAFT: [], ENDED: [] }
		for (const c of allRows) groups[deriveWindowStatus(c, now)].push(c)
		groups.ACTIVE.sort(
			(a, b) => (a.endsAt ?? Infinity) - (b.endsAt ?? Infinity) || (a.startsAt ?? 0) - (b.startsAt ?? 0)
		)
		groups.SCHEDULED.sort((a, b) => (a.startsAt ?? Infinity) - (b.startsAt ?? Infinity))
		groups.DRAFT.sort((a, b) => b.updatedAt - a.updatedAt)
		groups.ENDED.sort((a, b) => (b.endsAt ?? 0) - (a.endsAt ?? 0))
		return groups
	}, [allRows, now])

	const handleCreate = async () => {
		try {
			const result = await createContent({ body: { name: L.Marketing.Content.NewContent() } })
			if (result.isSuccess) {
				navigate({ to: '/marketing/content/$contentUuid', params: { contentUuid: result.response.uuid } })
			}
		} catch (e: any) {
			toast.error(e?.message ?? L.General.Unknown())
		}
	}

	const handleDuplicate = async (content: MarketingContent) => {
		try {
			const result = await duplicateContent({ path: { contentUuid: content.uuid } })
			if (result.isSuccess) {
				toast.success(L.Marketing.Content.DuplicateSuccess())
				navigate({ to: '/marketing/content/$contentUuid', params: { contentUuid: result.response.uuid } })
			}
		} catch (e: any) {
			toast.error(e?.message ?? L.General.Unknown())
		}
	}

	const handleDelete = async (content: MarketingContent) => {
		if (!window.confirm(L.Marketing.Content.DeleteConfirm())) return
		try {
			const result = await deleteContent({ path: { contentUuid: content.uuid } })
			if (!result.isSuccess) {
				toast.error(typeof result.response === 'string' ? result.response : L.General.Unknown())
			}
		} catch (e: any) {
			toast.error(e?.message ?? L.General.Unknown())
		}
	}

	const showingEmptyOverall = contentQuery.isSuccess && allRows.length === 0

	let listBody: ReactNode
	if (contentQuery.isError) {
		listBody = (
			<div className='flex flex-col items-center justify-center gap-3 rounded-md border border-dashed border-ui-border-base p-12 text-center'>
				<Heading level='h3'>{L.ApiErrorAlert.FailedToLoadData()}</Heading>
				<Text className='max-w-md text-ui-fg-subtle'>{L.ApiErrorAlert.AnErrorOccurredWhileFetchingData()}</Text>
				<Button type='button' variant='secondary' onClick={() => contentQuery.refetch()}>
					{L.General.TryAgain()}
				</Button>
			</div>
		)
	} else if (contentQuery.isLoading) {
		listBody = null
	} else if (showingEmptyOverall) {
		listBody = (
			<div className='flex flex-col items-center justify-center gap-3 rounded-md border border-dashed border-ui-border-base p-12 text-center'>
				<Calendar className='size-10 text-ui-fg-muted' />
				<Heading level='h3'>{L.Marketing.Content.NoContent()}</Heading>
				<Text className='max-w-sm text-ui-fg-subtle'>{L.Marketing.Content.NoContentDescription()}</Text>
				<Button onClick={handleCreate}>{L.Marketing.Content.NewContent()}</Button>
			</div>
		)
	} else {
		const renderRow = (content: MarketingContent, windowStatus: WindowStatus) => (
			<ContentRow
				key={content.uuid}
				content={content}
				windowStatus={windowStatus}
				onDuplicate={() => handleDuplicate(content)}
				onDelete={() => handleDelete(content)}
			/>
		)
		listBody = (
			<div className='flex flex-col gap-8'>
				<Section title={L.Marketing.Campaign.ActiveNow()} statusColor='green' items={grouped.ACTIVE}>
					{c => renderRow(c, 'ACTIVE')}
				</Section>
				<Section title={L.Marketing.Campaign.Scheduled()} statusColor='blue' items={grouped.SCHEDULED}>
					{c => renderRow(c, 'SCHEDULED')}
				</Section>
				<Section title={L.Marketing.Campaign.Draft()} statusColor='grey' items={grouped.DRAFT}>
					{c => renderRow(c, 'DRAFT')}
				</Section>
				<Section
					title={L.Marketing.Campaign.Ended()}
					statusColor='red'
					items={grouped.ENDED}
					collapsedByDefault
				>
					{c => renderRow(c, 'ENDED')}
				</Section>
			</div>
		)
	}

	return (
		<SingleColumnPage>
			<Container className='flex flex-1 flex-col overflow-hidden'>
				<ContainerHeader
					heading={L.Marketing.Content.Title()}
					rightSide={
						<IconButton tooltipText={L.Marketing.Content.NewContent()} onClick={handleCreate}>
							<Plus />
						</IconButton>
					}
				/>
				<div className='flex-1 overflow-y-auto p-4 sm:p-6'>{listBody}</div>
			</Container>
		</SingleColumnPage>
	)
}

type SectionProps = {
	title: string
	statusColor: 'green' | 'blue' | 'grey' | 'red'
	items: MarketingContent[]
	children: (item: MarketingContent) => ReactNode
	collapsedByDefault?: boolean
}

function Section({ title, statusColor, items, children, collapsedByDefault = false }: SectionProps) {
	const [collapsed, setCollapsed] = useState(collapsedByDefault)
	if (items.length === 0) return null
	return (
		<section>
			<button
				type='button'
				onClick={() => setCollapsed(!collapsed)}
				className='mb-2 flex items-center gap-2 text-left'
			>
				<StatusCell color={statusColor}>{title}</StatusCell>
				<Text className='text-ui-fg-subtle'>{items.length}</Text>
				<ChevronDown className={clx('size-4 transition-transform', collapsed && '-rotate-90')} />
			</button>
			{!collapsed && <div className='flex flex-col gap-1.5'>{items.map(item => children(item))}</div>}
		</section>
	)
}

function ContentRow({
	content,
	windowStatus,
	onDuplicate,
	onDelete
}: {
	content: MarketingContent
	windowStatus: WindowStatus
	onDuplicate: () => void
	onDelete: () => void
}) {
	const { L } = useI18n()
	return (
		<div className='group flex items-center gap-3 rounded-md border border-ui-border-base bg-ui-bg-base px-3 py-2.5 hover:bg-ui-bg-base-hover'>
			<Link
				to='/marketing/content/$contentUuid'
				params={{ contentUuid: content.uuid }}
				className='flex flex-1 items-center gap-4 text-left'
			>
				<div className='flex flex-1 flex-col'>
					<Text className='font-medium'>{content.name}</Text>
					<ScheduleSummary content={content} windowStatus={windowStatus} />
				</div>
				<div className='hidden text-right text-xs text-ui-fg-subtle sm:block'>
					{L.Marketing.Content.BlockCount({ count: content.blockCount })}
					{' · '}
					{content.layoutCount > 0
						? L.Marketing.Content.LayoutCount({ count: content.layoutCount })
						: L.Marketing.Content.NotInLayout()}
				</div>
			</Link>
			<IconButton
				variant='transparent'
				size='small'
				tooltipText={L.Marketing.Campaign.Action.Duplicate()}
				onClick={e => {
					e.stopPropagation()
					onDuplicate()
				}}
			>
				<Copy className='size-4 text-ui-fg-muted opacity-0 transition-opacity group-hover:opacity-100' />
			</IconButton>
			<IconButton
				variant='transparent'
				size='small'
				tooltipText={L.Marketing.Campaign.Action.Delete()}
				onClick={e => {
					e.stopPropagation()
					onDelete()
				}}
			>
				<Trash2 className='size-4 text-ui-fg-muted opacity-0 transition-opacity group-hover:opacity-100' />
			</IconButton>
		</div>
	)
}

function ScheduleSummary({ content, windowStatus }: { content: MarketingContent; windowStatus: WindowStatus }) {
	const { L, locale } = useI18n()
	const formatter = new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeStyle: 'short' })
	const fmt = (ms: number) => formatter.format(new Date(ms))

	if (windowStatus === 'DRAFT')
		return <Text className='text-xs text-ui-fg-muted'>{L.Marketing.Campaign.Draft()}</Text>
	if (windowStatus === 'SCHEDULED' && content.startsAt) {
		return (
			<Text className='text-xs text-ui-fg-subtle'>
				{L.Marketing.Campaign.Field.StartsAt()}: {fmt(content.startsAt)}
			</Text>
		)
	}
	if (windowStatus === 'ACTIVE') {
		return (
			<Text className='text-xs text-ui-fg-subtle'>
				{content.startsAt ? fmt(content.startsAt) : ''}
				{content.endsAt ? ` → ${fmt(content.endsAt)}` : ` → ${L.Marketing.Campaign.Field.EndsAtOpenEnded()}`}
			</Text>
		)
	}
	if (windowStatus === 'ENDED' && content.endsAt) {
		return (
			<Text className='text-xs text-ui-fg-muted'>
				{L.Marketing.Campaign.Field.EndsAt()}: {fmt(content.endsAt)}
			</Text>
		)
	}
	return null
}
